import type { Role } from '@/lib/api/types';

/**
 * RBAC matrix — mirrors docs/api/_shared/conventions.md and backend rbac.go exactly.
 * Used for menu visibility + control enablement ONLY; the server is authoritative and
 * every 403 is handled defensively (plan §4.3 / spec §4).
 *
 * `students:read` / `students:write` / `students:send` / `students:delete` are
 * held by `admin` and by NO other role. Round 10 split the old single student
 * write permission three ways so a key or a future role can edit records without
 * also emailing families or erasing them; `send` and `delete` also need an
 * interactive session on the server, so a `pdk_` key never reaches them.
 * That is not a default anyone may relax here: /privacy/ publishes "access to
 * applications is limited to the two people who run Svet", and this file plus
 * be-svet's rbac.go are the two places that sentence is either true or false.
 * Widening a role here only hides the 403 the server will still return — the
 * console would offer a minor's record and then fail to open it.
 *
 * `teach:own` (round 10, D-201) is the teacher's lens: the groups they teach,
 * through /admin/teach/* only. `teacher` holds it and nothing else; `admin`
 * does not hold it (an admin works groups through the cohort screens), and no
 * API key or connected app ever carries it.
 */
export type Permission =
  | 'content:read'
  | 'content:write'
  | 'content:publish'
  | 'content:delete'
  | 'content:translate'
  | 'media:manage'
  | 'leads:read'
  | 'leads:manage'
  | 'users:manage'
  | 'settings:manage'
  | 'audit:read'
  | 'students:read'
  | 'students:write'
  | 'students:send'
  | 'students:delete'
  | 'teach:own';

const ALL: Permission[] = [
  'content:read',
  'content:write',
  'content:publish',
  'content:delete',
  'content:translate',
  'media:manage',
  'leads:read',
  'leads:manage',
  'users:manage',
  'settings:manage',
  'audit:read',
  'students:read',
  'students:write',
  'students:send',
  'students:delete',
];

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: ALL,
  editor: [
    'content:read',
    'content:write',
    'content:publish',
    'content:translate',
    'media:manage',
    'leads:read',
    'leads:manage',
    'audit:read',
  ],
  author: ['content:read', 'content:write', 'content:translate', 'media:manage'],
  translator: ['content:read', 'content:translate'],
  sales: ['leads:read', 'leads:manage'],
  viewer: ['content:read', 'leads:read', 'audit:read'],
  teacher: ['teach:own'],
};

export function roleHas(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/** Human-readable label for tooltips on disabled controls (spec §4 rule of thumb). */
export const PERMISSION_LABELS: Record<Permission, string> = {
  'content:read': 'view content',
  'content:write': 'create or edit content',
  'content:publish': 'publish, schedule, or unpublish',
  'content:delete': 'delete content',
  'content:translate': 'edit translations',
  'media:manage': 'manage media',
  'leads:read': 'view leads',
  'leads:manage': 'manage leads',
  'users:manage': 'administer users',
  'settings:manage': 'manage site settings',
  'audit:read': 'view the audit log',
  'students:read': 'view student records and seminar applications',
  'students:write': 'edit student records and seminar applications (students:write)',
  'students:send': 'email students and parents (students:send)',
  'students:delete': 'erase student records and seminar applications (students:delete)',
  'teach:own': 'see and take the register of your own groups (teach:own)',
};
